import React from 'react';
import { createPortal } from 'react-dom';
import { usePortalContainer } from '@cotera/client/app/components/portal-container';
import { Icon } from '@cotera/client/app/components/ui/icon';
// `Button`'s own `icon` prop resolves `Icon` through the `components/ui` barrel,
// which re-exports this grid — the cycle hands it back undefined. Icons are
// passed as children off the direct module import above instead.
import { Button } from '@cotera/client/v0/components/ui/button';
import { Input } from '@cotera/client/v0/components/ui/input';
import { Textarea } from '@cotera/client/v0/components/ui/textarea';
import { cn } from '@cotera/client/v0/lib/utils';
import { dataGridColumnTypeIcon } from '../column-type';
import { formatDataGridValue, tryFormatJsonValue } from '../format';
import { DEFAULT_DATA_GRID_DISPLAY_OPTIONS } from '../types';
import type {
  DataGridCellContext,
  DataGridColumnDataType,
  DataGridEditorKind,
} from '../types';
import {
  dataGridEditorKind,
  parseEditorValue,
  toEditorValue,
} from './cell-editor';

/**
 * Whether the grid offers the expand-cell affordances — the per-cell button,
 * the footer indicator and the detail modal. Off by default so grids that never
 * opted in are untouched; the dataset preview and the chat transcript's
 * markdown tables turn it on. Read through context so `BaseCell` can gate its
 * button without every host threading a prop down to each cell.
 */
export const DataGridCellDetailEnabledContext = React.createContext(false);

const BOOLEAN_OPTIONS = ['true', 'false'] as const;

/**
 * The native `<select>` has no v0 counterpart we can drop in without changing
 * how the editor is labelled, so it borrows `Input`'s shape by hand. Every
 * other editor uses the shared component.
 */
const SELECT_CLASS =
  'border-input focus-visible:border-ring focus-visible:ring-ring/50 flex h-9 w-full min-w-0 cursor-pointer rounded-md border bg-transparent px-3 py-1 text-sm shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px]';

/**
 * Modal sizing, keyed by whether the value is long (see `isLongValue`).
 *
 * `panel` is the modal's width. Each entry carries its own `max-w` cap rather
 * than sharing one: two competing `max-w` utilities are resolved by stylesheet
 * order, not by the order they are written. Long is viewport-relative, so it
 * takes nearly the whole window on a laptop and still stops short of an
 * unreadably wide line on a large monitor. Short is 680px rather than the 416px
 * it used to be — that was cramped even for a sentence, and being the width the
 * grid already shipped it made opening a short cell look like nothing happened.
 *
 * `body` is the value's height, shared by the text editor and the read-only
 * view. It is a fixed `h-*` because that is what the editor has always really
 * been: a textarea does not grow with its content, so it sat at its `min-h` and
 * scrolled. A read-only `<div>` does grow, so with only the floor/ceiling a
 * long chat-table cell stretched to the ceiling while the same value in a
 * dataset opened at the floor. `min-h`/`max-h` stay as the editor's drag-resize
 * bounds.
 */
const DETAIL_SIZE = {
  long: {
    panel: 'w-[92vw] max-w-[1400px]',
    body: 'h-[380px] min-h-[380px] max-h-[76vh]',
  },
  short: {
    panel: 'w-[680px] max-w-[90vw]',
    body: 'h-[200px] min-h-[200px] max-h-[45vh]',
  },
} as const;

/** How long "Copied" stands before the button reverts to its resting label. */
const COPIED_RESET_MS = 1600;

/** The modal's title bar: the column it is expanding, and the way out. */
const DetailHeader: React.FC<{
  title: string;
  type: DataGridColumnDataType | undefined;
  typeLabel: string | undefined;
  onClose: () => void;
}> = ({ title, type, typeLabel, onClose }) => (
  <header className="flex items-start gap-2 border-b border-border px-4 py-3">
    <div className="flex min-w-0 flex-1 flex-col gap-1">
      {typeLabel !== undefined ? (
        <span className="flex min-w-0 items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          <Icon
            icon={dataGridColumnTypeIcon(type)}
            size="small"
            className="shrink-0"
          />
          <span className="truncate">{typeLabel}</span>
        </span>
      ) : null}
      <span className="truncate text-base font-semibold leading-none">
        {title}
      </span>
    </div>
    <Button
      variant="ghost"
      size="icon-sm"
      aria-label="Close"
      className="-mr-1 shrink-0 text-muted-foreground"
      onClick={onClose}
    >
      <Icon icon="x-mark" size="small" />
    </Button>
  </header>
);

/**
 * The expanded view of a single cell: its full value with room to read it, and
 * — when the cell is editable — an editor that commits back through the same
 * path an in-place edit uses. Rendered inside {@link DataGridCellDetailModal}.
 *
 * A column with a `renderDetail` of its own replaces the body wholesale, so a
 * cell whose value is a summary of something larger can expand into that
 * something rather than into the summary spelled out.
 */
export function DataGridCellDetail<TRow>({
  context,
  onClose,
}: {
  context: DataGridCellContext<TRow>;
  onClose: () => void;
}) {
  const { column, value } = context;
  // A column that knows how to present itself owns the whole body: the value
  // behind such a cell is a rolled-up code the cell already draws better than
  // the modal can print it, and "copy `never` to the clipboard" is not what a
  // reader who expanded it came for.
  const custom = column.renderDetail?.(context);
  const options = context.displayOptions ?? DEFAULT_DATA_GRID_DISPLAY_OPTIONS;
  const editable = context.state.editable;
  const editOptions = column.getEditOptions?.();
  const kind: DataGridEditorKind =
    column.editorKind ?? dataGridEditorKind(column.type, editOptions);
  const isBoolean = column.type === 'boolean';
  const formatted = formatDataGridValue(value, options, column.type);
  // A JSON object/array reads far better indented than as the single line the
  // grid packs into a cell; falls back to the plain value when it isn't JSON.
  const prettyJson = tryFormatJsonValue(value);
  const title =
    column.headerText ??
    (typeof column.header === 'string' ? column.header : column.id);

  const [draft, setDraft] = React.useState(() => toEditorValue(value, kind));
  const [copied, setCopied] = React.useState(false);

  /**
   * Whether this value is worth a big panel.
   *
   * The modal was one size for everything, which meant the size that suits
   * "Real Madrid" — and a generated email or an indented JSON document arrived
   * in a box a few lines tall that had to be scrolled to be read at all. Sized
   * off the content instead: long or multi-line values get the room, short ones
   * keep the compact panel rather than floating in an empty half-screen.
   *
   * The roomy layout takes most of the width that is there rather than a polite
   * slice of it, bounded by `max-w-[90vw]` so a narrow window is still
   * respected. Width is what a wrapped email actually needs: at this size a
   * generated pitch reads in a handful of lines instead of thirty.
   *
   * Height is fixed per size and scrolls past it; see `DETAIL_SIZE`.
   *
   * Sizes in px, not rem, for the reason `--container-list-cols` gives in
   * `styles.css`: this app sets `html { font-size: 0.8125rem }`, so a rem is
   * 13px and every rem width here came out a quarter smaller than it read.
   */
  const body = prettyJson ?? formatted ?? draft;
  const isLongValue = body.length > 240 || body.includes('\n');
  const size = DETAIL_SIZE[isLongValue ? 'long' : 'short'];

  // The confirmation is a flash, not a new resting state: without this the
  // button reads "Copied" for as long as the modal stays open.
  React.useEffect(() => {
    if (!copied) {
      return;
    }
    const timer = setTimeout(() => setCopied(false), COPIED_RESET_MS);
    return () => clearTimeout(timer);
  }, [copied]);

  const commit = () => {
    context.commands.commitEdit(parseEditorValue(draft, kind));
    onClose();
  };

  const copy = () => {
    void navigator.clipboard
      ?.writeText(formatted ?? '')
      .then(() => setCopied(true))
      .catch(() => undefined);
  };

  const selectOptions =
    isBoolean && (editOptions === undefined || editOptions.length === 0)
      ? BOOLEAN_OPTIONS
      : editOptions ?? [];

  const renderEditor = () => {
    if (kind === 'select') {
      return (
        <select
          autoFocus
          aria-label="Edit cell"
          className={SELECT_CLASS}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
        >
          <option value="">—</option>
          {selectOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      );
    }
    if (kind === 'text') {
      return (
        <Textarea
          autoFocus
          aria-label="Edit cell"
          className={cn(
            'resize-y font-mono text-sm leading-relaxed',
            size.body
          )}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
        />
      );
    }
    return (
      <Input
        autoFocus
        aria-label="Edit cell"
        type={kind === 'number' ? 'number' : 'date'}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
      />
    );
  };

  if (custom !== undefined) {
    return (
      <div className="flex w-[44rem] max-w-[90vw] flex-col">
        <DetailHeader
          title={title}
          type={column.type}
          typeLabel={column.typeLabel}
          onClose={onClose}
        />
        <div className="max-h-[60vh] overflow-y-auto">{custom}</div>
        <footer className="flex items-center justify-end border-t border-border px-4 py-3">
          <Button variant="outline" size="sm" onClick={onClose}>
            Close
          </Button>
        </footer>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col', size.panel)}>
      <DetailHeader
        title={title}
        type={column.type}
        typeLabel={column.typeLabel}
        onClose={onClose}
      />
      <div className="px-4 py-3">
        {editable ? (
          renderEditor()
        ) : formatted === null ? (
          <span className="rounded-md bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
            null
          </span>
        ) : (
          <div
            className={cn(
              'overflow-auto whitespace-pre-wrap break-words rounded-md border border-border bg-muted/30 p-3 font-mono text-sm leading-relaxed',
              size.body
            )}
          >
            {prettyJson ?? formatted}
          </div>
        )}
      </div>
      <footer className="flex items-center justify-between gap-2 border-t border-border px-4 py-3">
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground"
          onClick={copy}
        >
          <Icon
            icon={copied ? 'clipboard-document-check' : 'clipboard-document'}
            size="small"
          />
          {copied ? 'Copied' : 'Copy'}
        </Button>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>
            {editable ? 'Cancel' : 'Close'}
          </Button>
          {editable ? (
            <Button size="sm" onClick={commit}>
              Save
            </Button>
          ) : null}
        </div>
      </footer>
    </div>
  );
}

/**
 * Centred modal host for {@link DataGridCellDetail}. Portalled so it escapes the
 * grid's own overflow clipping, and closes on Escape or a backdrop click. Kept
 * separate from the anchored {@link DataGridOverlay} because an expanded cell
 * wants room and focus rather than to sit tucked under its origin.
 *
 * Hand-rolled rather than reaching for the v0 `Dialog` so it can portal into the
 * app's themed {@link usePortalContainer}; the panel deliberately wears the same
 * surface, radius and entrance as `DialogContent`.
 */
export function DataGridCellDetailModal({
  children,
  onClose,
}: {
  children: React.ReactNode;
  onClose: () => void;
}) {
  const portalContainer = usePortalContainer();

  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const root = portalContainer ?? document.body;
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        aria-hidden
        className="absolute inset-0 bg-gray-900/50 animate-in fade-in-0 duration-200"
        onClick={onClose}
      />
      <div
        className={cn(
          'relative z-10 overflow-hidden rounded-lg border border-border bg-background text-foreground shadow-lg',
          'animate-in fade-in-0 zoom-in-95 duration-200'
        )}
      >
        {children}
      </div>
    </div>,
    root
  );
}
